import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link2, Unlink, Undo2, Redo2,
} from "lucide-react";

/**
 * Lightweight rich-text editor for business emails.
 *
 * Built on a native contentEditable surface + document.execCommand so it stays
 * dependency-free and emits clean, email-safe HTML (<strong>/<em>/<u>,
 * <ul>/<ol><li>, <a href>, <p>/<br>). Intentionally minimal — this is for
 * normal personalized emails, not a newsletter designer.
 *
 * Final HTML is ALWAYS sanitized again on the backend before it is stored or
 * rendered, so the editor output is never trusted blindly.
 *
 * Imperative API (via ref):
 *   • focus()              – focus the editing surface
 *   • insertToken(token)   – insert a merge field at the caret without breaking
 *                            surrounding formatting
 */
const ToolBtn = ({ onDo, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    // preventDefault keeps the current selection inside the editor when the
    // toolbar button is pressed.
    onMouseDown={(e) => {
      e.preventDefault();
      onDo();
    }}
    className="h-8 w-8 inline-flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
  >
    {children}
  </button>
);

export const RichTextEditor = forwardRef(
  ({ value, onChange, placeholder = "Write your email…", testid = "rich-text-editor" }, ref) => {
    const elRef = useRef(null);

    // Sync incoming value into the DOM only when the editor is NOT focused,
    // so external updates (e.g. loading a template) apply without stealing the
    // caret while the user types.
    useEffect(() => {
      const el = elRef.current;
      if (!el) return;
      const next = value || "";
      if (next !== el.innerHTML && document.activeElement !== el) {
        el.innerHTML = next;
      }
    }, [value]);

    const emit = () => {
      const el = elRef.current;
      if (el && onChange) onChange(el.innerHTML);
    };

    const exec = (cmd, arg = null) => {
      const el = elRef.current;
      if (el) el.focus();
      try {
        document.execCommand(cmd, false, arg);
      } catch {
        /* no-op */
      }
      emit();
    };

    const addLink = () => {
      const el = elRef.current;
      if (el) el.focus();
      const sel = window.getSelection();
      const selectedText = sel ? sel.toString() : "";
      let url = window.prompt("Enter the web address (URL):", "https://");
      if (!url) return;
      url = url.trim();
      if (!/^(https?:\/\/|mailto:)/i.test(url)) url = `https://${url}`;
      if (selectedText) {
        document.execCommand("createLink", false, url);
      } else {
        // No selection: insert the URL itself as a clickable link.
        const safeText = url.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        document.execCommand("insertHTML", false, `<a href="${url}">${safeText}</a>`);
      }
      emit();
    };

    useImperativeHandle(ref, () => ({
      focus: () => elRef.current && elRef.current.focus(),
      insertToken: (token) => {
        const el = elRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
          document.execCommand("insertText", false, token);
        } else {
          el.appendChild(document.createTextNode(token));
        }
        emit();
      },
    }));

    return (
      <div
        className="rounded-lg border border-white/10 bg-[#0A0F1E] overflow-hidden"
        data-testid={testid}
      >
        <div className="flex items-center gap-0.5 border-b border-white/10 px-1.5 py-1 flex-wrap">
          <ToolBtn onDo={() => exec("bold")} title="Bold" testid="rte-bold">
            <Bold size={15} />
          </ToolBtn>
          <ToolBtn onDo={() => exec("italic")} title="Italic" testid="rte-italic">
            <Italic size={15} />
          </ToolBtn>
          <ToolBtn onDo={() => exec("underline")} title="Underline" testid="rte-underline">
            <UnderlineIcon size={15} />
          </ToolBtn>
          <span className="mx-1 h-5 w-px bg-white/10" />
          <ToolBtn onDo={() => exec("insertUnorderedList")} title="Bulleted list" testid="rte-ul">
            <List size={15} />
          </ToolBtn>
          <ToolBtn onDo={() => exec("insertOrderedList")} title="Numbered list" testid="rte-ol">
            <ListOrdered size={15} />
          </ToolBtn>
          <span className="mx-1 h-5 w-px bg-white/10" />
          <ToolBtn onDo={addLink} title="Insert link" testid="rte-link">
            <Link2 size={15} />
          </ToolBtn>
          <ToolBtn onDo={() => exec("unlink")} title="Remove link" testid="rte-unlink">
            <Unlink size={15} />
          </ToolBtn>
          <span className="mx-1 h-5 w-px bg-white/10" />
          <ToolBtn onDo={() => exec("undo")} title="Undo" testid="rte-undo">
            <Undo2 size={15} />
          </ToolBtn>
          <ToolBtn onDo={() => exec("redo")} title="Redo" testid="rte-redo">
            <Redo2 size={15} />
          </ToolBtn>
        </div>
        <div
          ref={elRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Email body"
          data-testid="rte-content"
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
          className="rd-rich-content rd-rte-content min-h-[180px] max-h-[360px] overflow-y-auto px-3 py-2.5 text-sm leading-relaxed text-white/90 focus:outline-none"
        />
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
